// ============================================
// STAT CARD COMPONENT
// ============================================

import { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { cn, formatNumber } from '@/utils';

interface StatCardProps {
  title: string;
  value: number | string;
  icon?: ReactNode;
  iconColor?: string;
  change?: {
    value: number;
    label?: string;
  };
  loading?: boolean;
  className?: string;
}

export function StatCard({
  title,
  value,
  icon,
  iconColor = 'bg-maritime-100 text-maritime-600',
  change,
  loading = false,
  className,
}: StatCardProps) {
  const formattedValue = typeof value === 'number' ? formatNumber(value) : value;
  const isPositive = change && change.value >= 0;

  return (
    <div
      className={cn(
        'bg-white rounded-maritime border border-gray-200 p-6',
        loading && 'animate-pulse',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          {loading ? (
            <div className="mt-2 h-8 w-24 bg-gray-200 rounded" />
          ) : (
            <p className="mt-2 text-3xl font-bold text-gray-900">{formattedValue}</p>
          )}
          {change && !loading && (
            <div className="mt-2 flex items-center gap-1">
              {isPositive ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
              <span
                className={cn(
                  'text-sm font-medium',
                  isPositive ? 'text-green-600' : 'text-red-600'
                )}
              >
                {isPositive ? '+' : ''}{change.value}%
              </span>
              {change.label && (
                <span className="text-sm text-gray-500">{change.label}</span>
              )}
            </div>
          )}
        </div>
        {icon && (
          <div className={cn('flex-shrink-0 p-3 rounded-maritime', iconColor)}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}
